import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { usePwlJugStillMissing } from '@hooks/register/use-pwl-jug-still-missing';
import { useLocalize, type TLocalize } from '@hooks/use-localize';
import { useSession } from '@hooks/security/use-session';
import type { IPwlJugStillMissingModel } from '@/types/history/pwl-jug-still-missing';
import PageLoader from '@components/page-loader';

const PwlJugStillMissing = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, sendAnother, isSubmitting, submitError } = usePwlJugStillMissing(registrationId, editKey);

    return (
        <div className="container-fluid">
            <PageLoader isLoading={isLoading} error={error}>
                {!error && (
                    <PwlJugStillMissingContent
                        model={model}
                        registrationId={registrationId}
                        editKey={editKey}
                        onSendAnother={sendAnother}
                        isSubmitting={isSubmitting}
                        submitError={submitError}
                    />
                )}
            </PageLoader>
        </div>
    );
};

interface IPwlJugStillMissingContentProps {
    model: IPwlJugStillMissingModel;
    registrationId: number;
    editKey: string;
    onSendAnother: () => Promise<void>;
    isSubmitting: boolean;
    submitError: string | null;
}

const PwlJugStillMissingContent = ({
    model,
    registrationId,
    editKey,
    onSendAnother,
    isSubmitting,
    submitError,
}: IPwlJugStillMissingContentProps) => {
    const t = useLocalize(model.languageData);

    // The instructions page is still Razor, so it needs a full navigation.
    const razorQuery = `?id=${registrationId}&key=${editKey}`;

    return (
        <div className="row row-margined">
            <div className="col-xs-12 col-sm-7 col-sm-offset-0">
                {model.status === 'ask' && (
                    <>
                        <div className="row row-margined">
                            <div className="col-xs-12">
                                <h1>{t('DashLabel_WeApologizeForThisInconvenience', 'We apologize for this inconvenience')}.</h1>
                                <p>
                                    {t(
                                        'DashLabel_WouldYouLikeUsToSendYouAnother24HourUrineCollectionJugSoThat',
                                        'Would you like us to send you another 24-hour urine collection jug so that you can procede with the lab testing process',
                                    )}
                                    ?
                                </p>
                            </div>
                        </div>

                        {submitError && (
                            <div className="row row-margined">
                                <div className="col-xs-12">
                                    <div className="alert alert-danger">{submitError}</div>
                                </div>
                            </div>
                        )}

                        <ActionButtons t={t}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                style={{ marginRight: 10 }}
                                disabled={isSubmitting}
                                onClick={() => void onSendAnother()}
                            >
                                {t('DashLabel_YesSendMeAnotherJug', 'Yes, send me another jug')}
                            </button>
                        </ActionButtons>
                    </>
                )}

                {model.status === 'shipped' && (
                    <>
                        <div className="row row-margined">
                            <div className="col-xs-12">
                                <h1>{t('DashLabel_ThankYou', 'Thank You')}</h1>
                                <p>
                                    {t(
                                        'DashLabel_ThankYouA24HourUrineCollectionJugHasBeenOrderedForYouAndShould',
                                        'Thank you, a 24-hour urine collection jug has been ordered for you and should arrive within the next two weeks',
                                    )}
                                    .
                                </p>
                                <p>
                                    {t(
                                        'DashLabel_OnceShippedOurSystemWillTrackThisPackageWhenItIsMarkedAsArrived',
                                        'Once shipped, our system will track this package. When it is marked as arrived, we will send you a confirmation email, which will allow you to acknowledge receipt or notify us if you did not receive it. Once acknowledged, we will provide you with instructions on how to complete a 24-hour urine test as well as the rest of the tests (blood draw, height, weight, blood pressure and urine sample). If you would like to read the instructions to better prepare yourself, click the link below',
                                    )}
                                    .
                                </p>
                            </div>
                        </div>

                        <ActionButtons t={t} isDashboardPrimary>
                            <a className="btn btn-default" style={{ marginRight: 10 }} href={'/register/pwl-jug-instructions' + razorQuery}>
                                {t('DashLabel_24HourUrineCollectionInstructions', '24-hour urine collection instructions')}
                            </a>
                        </ActionButtons>
                    </>
                )}

                {model.status === 'limit' && (
                    <>
                        <div className="row row-margined">
                            <div className="col-xs-12">
                                <h1>{t('DashLabel_WeApologizeForTheInconvinence', 'We apologize for the inconvinence')}.</h1>
                                <p>
                                    {t(
                                        'DashLabel_UnfortunatelyOurPolicyIsToSendAMaximumOf1ReplacementJugAfterTheOriginal',
                                        'Unfortunately our policy is to send a maximum of 1 replacement jug after the original is lost.  If you would still like to proceed with the lab testing process you will need to get your own jug from a doctor, medical supply store, online, etc. as we cannot send you a third jug. If you choose to continue we will send you an email to see if you have acquired a 24-hour urine collection jug.  Once you have, we will proceed normally and you will not have to pay for any of the subsequent steps',
                                    )}
                                    .
                                </p>
                                <p>{t('DashLabel_WouldYouStillLikeToProceed', 'Would you still like to proceed')}?</p>
                            </div>
                        </div>

                        <ActionButtons t={t}>
                            <Link className="btn btn-primary" style={{ marginRight: 10 }} to="/register/pwl-jug-self">
                                {t('DashLabel_YesIWillGetMyOwnJug', 'Yes, I will get my own jug')}
                            </Link>
                        </ActionButtons>
                    </>
                )}
            </div>
        </div>
    );
};

interface IActionButtonsProps {
    t: TLocalize;
    isDashboardPrimary?: boolean;
    children: ReactNode;
}

const ActionButtons = ({ t, isDashboardPrimary = false, children }: IActionButtonsProps) => (
    <>
        <div className="row row-margined">
            <div className="col-xs-12">
                {children}

                <Link className={isDashboardPrimary ? 'btn btn-primary' : 'btn btn-default'} to="/register/donate">
                    {t('DashLabel_BackToDashboard', 'Back to Dashboard')}
                </Link>
            </div>
        </div>

        <div className="journey-opt-out">
            You may withdraw at any time using this link: <Link to="/register/opt-out">Opt-out of the donation process</Link>
        </div>
    </>
);

export default PwlJugStillMissing;
